// packages/constants/src/ratings.js
//
// Aluvo star ratings ("how much did you enjoy this movie?"), shared by web and
// mobile. The backend mirrors the scale and rounding in
// apps/backend/src/utils/ratingScale.js; a backend test evaluates this file
// and fails if the two drift.
//
// Ratings are independent of challenges and coins: no coin is needed to rate,
// and every rating counts the same in the community average.
//
// Scale: 0.5 … 5 stars in 0.5 steps. Stored server-side as integer half-star
// units (1 … 10) so nothing persisted is ever a float. The API speaks stars.
//
// Community average: unweighted, one decimal, rounded half-up, and only shown
// from MIN_COMMUNITY_RATINGS ratings. Below that the API returns
// `average: null` and clients show the count only. Never seeded from TMDB.

export const MIN_RATING = 0.5;
export const MAX_RATING = 5;
export const RATING_STEP = 0.5;
export const MIN_COMMUNITY_RATINGS = 3;
export const MAX_RATING_HALF = MAX_RATING / RATING_STEP; // 10

/** True for a JSON number in 0.5 … 5 on a 0.5 step. Strings, NaN, booleans: false. */
export function isValidRating(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) return false;
    const half = value * 2;
    return Number.isInteger(half) && half >= 1 && half <= MAX_RATING_HALF;
}

/** 4.5 → 9. Returns null for anything isValidRating rejects. */
export function starsToHalf(stars) {
    return isValidRating(stars) ? stars * 2 : null;
}

/** 9 → 4.5. Returns null for anything outside 1 … 10. */
export function halfToStars(half) {
    const n = Number(half);
    return Number.isInteger(n) && n >= 1 && n <= MAX_RATING_HALF ? n / 2 : null;
}

/**
 * Community average to one decimal, rounded half-up, from integer half-star
 * units, using integer arithmetic only: avg = sum / (2·count), so
 * round(avg·10) = floor((10·sum + count) / (2·count)).
 * (26 half-units over 3 ratings → 4.3.) Null for no ratings.
 */
export function averageFromHalfUnits(sumHalf, count) {
    const n = Number(count);
    const sum = Number(sumHalf);
    if (!Number.isInteger(n) || n <= 0 || !Number.isFinite(sum)) return null;
    return Math.floor((10 * sum + n) / (2 * n)) / 10;
}

/** Snap to the nearest valid rating inside the scale (keyboard / drag input). */
export function clampRating(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return MIN_RATING;
    const snapped = Math.round(n * 2) / 2;
    return Math.min(MAX_RATING, Math.max(MIN_RATING, snapped));
}

/** One step up or down from `current` (null = unrated: up starts at 0.5). */
export function stepRating(current, direction) {
    if (current == null) return direction > 0 ? MIN_RATING : null;
    return clampRating(current + (direction > 0 ? RATING_STEP : -RATING_STEP));
}

/**
 * Rating for a pointer at `fraction` (0 … 1) across a five-star row: the left
 * half of a star is .5, the right half is the whole star. Shared by the web
 * hover / click and the mobile tap / drag so both pick the same value.
 */
export function ratingFromFraction(fraction) {
    const f = Number(fraction);
    if (!Number.isFinite(f)) return MIN_RATING;
    return clampRating(Math.ceil(Math.min(1, Math.max(0, f)) * MAX_RATING_HALF) / 2);
}

/**
 * How full each of the five stars is for `rating`: 'full' | 'half' | 'empty'.
 * null / 0 → five empty stars.
 */
export function starFills(rating) {
    const half = rating == null ? 0 : Math.round(Number(rating) * 2);
    return Array.from({ length: MAX_RATING }, (_, i) => {
        const filled = half - i * 2;
        if (filled >= 2) return 'full';
        if (filled === 1) return 'half';
        return 'empty';
    });
}

/** 4.5 → "4.5", 4 → "4.0". Ratings and averages always show one decimal. */
export function formatRating(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(1) : '';
}

/** "4.5 stars", "1.0 star" — for screen readers and readouts. */
export function ratingAccessibilityText(value) {
    if (value == null) return 'Not rated';
    return `${formatRating(value)} ${Number(value) === 1 ? 'star' : 'stars'}`;
}

/** 1284 → "1,284" (fixed en-US grouping so web and mobile render the same). */
export function formatRatingCount(count) {
    const n = Number(count);
    return (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0).toLocaleString('en-US');
}

/**
 * The community line for a title, from the API's `aluvoRating`
 * ({ average, count }). Returns null when the aggregate could not be loaded
 * (render nothing rather than a false "no ratings").
 *   0      → { showAverage: false, text: 'No Aluvo ratings yet' }
 *   1 – 2  → { showAverage: false, text: '2 Aluvo ratings' }
 *   ≥ 3    → { showAverage: true,  text: 'Aluvo ★ 4.3 · 128 ratings', average: '4.3', countText: '128 ratings' }
 */
export function communityRatingView(aluvoRating) {
    if (!aluvoRating || typeof aluvoRating !== 'object') return null;
    const count = Number.isFinite(Number(aluvoRating.count)) && Number(aluvoRating.count) > 0
        ? Math.floor(Number(aluvoRating.count))
        : 0;
    if (count === 0) {
        return { showAverage: false, count, average: null, countText: null, text: 'No Aluvo ratings yet' };
    }
    const countText = `${formatRatingCount(count)} ${count === 1 ? 'rating' : 'ratings'}`;
    const average = Number(aluvoRating.average);
    if (count < MIN_COMMUNITY_RATINGS || aluvoRating.average == null || !Number.isFinite(average)) {
        return { showAverage: false, count, average: null, countText, text: `${formatRatingCount(count)} Aluvo ${count === 1 ? 'rating' : 'ratings'}` };
    }
    const avgText = formatRating(average);
    return { showAverage: true, count, average: avgText, countText, text: `Aluvo ★ ${avgText} · ${countText}` };
}

/**
 * Optimistic community aggregate after the viewer changes their own rating
 * from `previous` to `next` (either may be null). Only the count is adjusted:
 * the average below the threshold is hidden by the server, so clients never
 * try to recompute it — the server's response replaces this value.
 */
export function optimisticAggregate(aluvoRating, previous, next) {
    if (!aluvoRating) return aluvoRating;
    const count = Math.max(0, Number(aluvoRating.count || 0) + (previous == null && next != null ? 1 : 0) - (previous != null && next == null ? 1 : 0));
    return { ...aluvoRating, count, average: count >= MIN_COMMUNITY_RATINGS ? aluvoRating.average : null };
}

/** Auth-gate intent type for a guest who tried to rate. */
export const RATE_TITLE_INTENT = 'RATE_TITLE';
